import styled, { css } from 'styled-components';
import {
  CARD_PICKER_DENSITY_TOKENS,
  type CardPickerDensity,
} from './CardPickerModal.constants';

export const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  /* Cap the body height so the grid scrolls before the modal grows
     past the viewport. The Modal organism already centres us, so the
     cap is relative to the viewport. */
  max-height: min(70vh, 640px);
`;

export const SearchRow = styled.div`
  flex-shrink: 0;
`;

export const ScrollArea = styled.div`
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 auto;
  /* A negative margin here overflows the Modal body, which then scrolls sideways. */
  padding-right: 4px;
`;

interface GridProps {
  $density: CardPickerDensity;
}

export const Grid = styled.div<GridProps>`
  display: grid;
  ${({ $density }) => {
    const tokens = CARD_PICKER_DENSITY_TOKENS[$density];
    return css`
      grid-template-columns: repeat(
        auto-fill,
        minmax(${tokens.minCardWidth}, 1fr)
      );
      gap: ${tokens.gap};
    `;
  }}
`;

interface CardProps {
  $density: CardPickerDensity;
  $selected: boolean;
  $disabled: boolean;
}

/**
 * Card uses a native `<button>` so keyboard activation comes for free.
 * `color: inherit` defeats the UA `color: buttontext` so any tone we
 * paint via `currentColor` (e.g. selection ring) inherits correctly.
 */
export const Card = styled.button<CardProps>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  cursor: pointer;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.large ?? borderRadius.small ?? '12px'};
  border: 1.5px solid;
  ${({ $density }) => {
    const tokens = CARD_PICKER_DENSITY_TOKENS[$density];
    return css`
      padding: ${tokens.cardPadY} ${tokens.cardPadX};
      gap: 8px;
    `;
  }}
  ${({ $selected, theme: { colors = {} } }) =>
    $selected
      ? css`
          background: color-mix(
            in oklch,
            ${colors.primary ?? '#C1C1FF'} 14%,
            transparent
          );
          border-color: ${colors.primary ?? '#C1C1FF'};
        `
      : css`
          background: ${colors.surfaceContainer ?? '#1F1F27'};
          border-color: ${colors.outlineVariant ?? '#464555'};
        `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.55;
      pointer-events: none;
    `}

  ${({ theme: { motion } }) => css`
    transition:
      background ${motion?.duration?.fast ?? '120ms'}
        ${motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'},
      border-color ${motion?.duration?.fast ?? '120ms'}
        ${motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'},
      transform ${motion?.duration?.fast ?? '120ms'}
        ${motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};
  `}

  &:hover:not(:disabled) {
    background: ${({ $selected, theme: { colors = {} } }) =>
      $selected
        ? `color-mix(in oklch, ${colors.primary ?? '#C1C1FF'} 22%, transparent)`
        : `color-mix(in oklch, ${colors.onSurface ?? '#E4E1ED'} 6%, transparent)`};
    border-color: ${({ $selected, theme: { colors = {} } }) =>
      $selected
        ? colors.primary ?? '#C1C1FF'
        : colors.outline ?? colors.onSurfaceVariant ?? '#787685'};
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }
`;

export const CardHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

export const CardIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.small ?? '8px'};
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHighest ?? colors.surface ?? '#262631'};
  color: ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
  flex-shrink: 0;

  & > svg,
  & > .material-symbols-rounded {
    width: 22px;
    height: 22px;
    font-size: 22px;
  }
`;

export const CardTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
  line-height: 1.3;
`;

export const CardDescription = styled.span`
  font-size: 12.5px;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
  line-height: 1.4;
`;

export const CardBadgeSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 4px;
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const SelectionDot = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid;
  flex-shrink: 0;
  ${({ $selected, theme: { colors = {} } }) => {
    const primary = colors.primary ?? '#C1C1FF';
    return $selected
      ? css`
          background: ${primary};
          border-color: ${primary};
          color: ${colors.onPrimary ?? '#13131B'};
        `
      : css`
          background: transparent;
          border-color: ${colors.outlineVariant ?? '#464555'};
          color: transparent;
        `;
  }}
  & > svg {
    width: 12px;
    height: 12px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
  font-size: 13px;
`;

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const FooterLeft = styled.span`
  font-size: 12.5px;
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const FooterRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
